/**
 * The conversation on this screen (unit 26B; v1's `chatHistory`,
 * `agentTyping` and `agentStatus`). In one store, not in a component, because
 * the chat region is rendered in more than one place — the launcher's column,
 * under a widget — and a draft must survive the move between them.
 */
import { create } from "zustand";

import {
  addOlder,
  addPart,
  cleared,
  foldReply,
  lineKey,
  putJob,
  putRequest,
  resolveRequest,
  settle,
  showEarlier,
  type Job,
  type Line,
  type Request,
  type Status,
  type ThreadItem,
} from "./fold";

/** The earlier thread, once `/history` has shown it: where the next older
 * page starts, and whether one is being read. None until the command. */
export interface Earlier {
  next: number | null;
  loading: boolean;
}

/** What the thinking line says (v1.98): one line for the run, not one per message. */
export type Activity = "sending" | "thinking" | "joined" | "working";

interface Chat {
  lines: Line[];
  running: boolean;
  activity: Activity;
  /** What the agent is doing now, while a tool runs (#85): the server's words, or null. */
  step: string | null;
  draft: string;
  earlier: Earlier | null;
  /** `/history`: the newest page replaces the thread. */
  showEarlier(page: readonly ThreadItem[], next: number | null): void;
  /** An older page, above. */
  addOlder(page: readonly ThreadItem[], next: number | null): void;
  loadingEarlier(loading: boolean): void;
  setDraft(draft: string): void;
  /** A line from the app itself: a command's answer, or an error. */
  say(content: string, error?: boolean): void;
  addUser(content: string): string;
  mark(key: string, status: Status): void;
  start(activity: Activity): void;
  part(content: string): void;
  /** The server's words for the tool now running (`agent_step`). */
  stepped(words: string): void;
  reply(content: string, id: number | null): void;
  /** A write the agent asks for, as a line; and its answer. */
  request(request: Request): void;
  resolved(id: number, status: Request["status"], error?: string | null): void;
  /** A job a person started: its box, new or moved a step. */
  job(job: Job): void;
  settle(): void;
  clear(): void;
}

export const useChat = create<Chat>()((set) => ({
  lines: [],
  running: false,
  activity: "thinking",
  step: null,
  draft: "",
  earlier: null,
  showEarlier: (page, next) =>
    set((s) => ({ lines: showEarlier(s.lines, page), earlier: { next, loading: false } })),
  addOlder: (page, next) =>
    set((s) => ({ lines: addOlder(s.lines, page), earlier: { next, loading: false } })),
  loadingEarlier: (loading) =>
    set((s) => (s.earlier ? { earlier: { ...s.earlier, loading } } : {})),
  setDraft: (draft) => set({ draft }),
  say: (content, error = false) =>
    set((s) => ({
      lines: [
        ...s.lines,
        { key: lineKey(), role: "assistant", content, id: null, notice: true, error },
      ],
    })),
  addUser: (content) => {
    const key = lineKey();
    set((s) => ({
      lines: [...s.lines, { key, role: "user", content, id: null, status: "sending" }],
    }));
    return key;
  },
  mark: (key, status) =>
    set((s) => ({ lines: s.lines.map((l) => (l.key === key ? { ...l, status } : l)) })),
  start: (activity) => set({ running: true, activity, step: null }),
  // A part means a run is going, even one this screen did not start.
  part: (content) =>
    set((s) => ({
      lines: addPart(s.lines, content),
      running: true,
      activity: "working",
      step: null,
    })),
  stepped: (words) => set({ running: true, step: words }),
  reply: (content, id) => set((s) => ({ lines: foldReply(s.lines, content, id) })),
  request: (request) => set((s) => ({ lines: putRequest(s.lines, request) })),
  resolved: (id, status, error = null) =>
    set((s) => ({ lines: resolveRequest(s.lines, id, status, error) })),
  job: (job) => set((s) => ({ lines: putJob(s.lines, job) })),
  settle: () =>
    set((s) => ({ lines: settle(s.lines), running: false, activity: "thinking", step: null })),
  clear: () => set((s) => ({ lines: cleared(s.lines), earlier: null })),
}));
